import type { FormEvent, MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { SearchMenuStates } from 'components/Search/type'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import { buildDisplayOnlySearchItem, getSubmitSearchItem } from '../components/ListingDrawerSearchUtils'
import { getExperienceDateRangeFromSearchState, getValidExperienceDateRange } from '../components/ExperienceListingDrawer/experienceSearchDates'
import type { ExperienceDateRange } from 'tripPlanner/utils/resolveSearchDefaults'
import type { ExperienceListingSearchSubmitParams } from '../components/ExperienceListingDrawer/useExperienceListingSearchState'

export interface ExperienceListingSearchControlsParams {
  searchValue: string
  searchItem?: App.SearchItem
  checkIn?: string
  checkOut?: string
  onDateChange: (dates: ExperienceDateRange) => void
  onSearchSubmit: (params: ExperienceListingSearchSubmitParams) => void
  whenLabel?: string
  experienceTripDateRange?: { start: string, end: string }
}

function useExperienceListingSearchControls({ searchValue, searchItem, checkIn, checkOut, onDateChange, onSearchSubmit, whenLabel, experienceTripDateRange }: ExperienceListingSearchControlsParams) {
  const intl = useIntl()
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const experienceDateTriggerRef = useRef<HTMLButtonElement>(null)
  const isCompactDatePicker = !useScreenSizeUp('tablet')
  const [isExperienceDatePanelOpen, setExperienceDatePanelOpen] = useState(false)
  const committedDates = useMemo(() => getValidExperienceDateRange({ checkIn, checkOut }), [checkIn, checkOut])
  const initialState = useMemo(() => ({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue),
    checkinDate: committedDates.checkIn ? moment(committedDates.checkIn) : undefined,
    checkoutDate: committedDates.checkOut ? moment(committedDates.checkOut) : undefined,
    dateSearchOptionId: committedDates.checkIn ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME,
    isAnytimeDateSelected: !committedDates.checkIn,
  }), [committedDates, searchItem, searchValue])
  const { globalSearchState: searchState, globalSearchDispatch: searchDispatch } = useGlobalSearchContext(initialState)
  const restoreCommittedDates = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: committedDates.checkIn ? moment(committedDates.checkIn) : undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: committedDates.checkOut ? moment(committedDates.checkOut) : undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: committedDates.checkIn ? DATE_SEARCH_OPTION_IDS.SPECIFIC : DATE_SEARCH_OPTION_IDS.ANYTIME })
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: !committedDates.checkIn })
  }, [committedDates, searchDispatch])
  useEffect(() => {
    if (!isExperienceDatePanelOpen && searchState.activeMenu !== SearchMenuStates.Dates) restoreCommittedDates()
  }, [isExperienceDatePanelOpen, restoreCommittedDates, searchState.activeMenu])
  useEffect(() => {
    if (!isCompactDatePicker) setExperienceDatePanelOpen(false)
  }, [isCompactDatePicker])
  useEffect(() => {
    searchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem: searchItem ?? buildDisplayOnlySearchItem(searchValue) })
  }, [searchDispatch, searchItem, searchValue])

  const openDates = useCallback((event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation()
    if (isCompactDatePicker) setExperienceDatePanelOpen(true)
    else searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Dates })
  }, [isCompactDatePicker, searchDispatch])
  const closeMenu = useCallback(() => {
    setExperienceDatePanelOpen(false)
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [searchDispatch])
  const cancelDates = useCallback(() => {
    restoreCommittedDates()
    closeMenu()
  }, [closeMenu, restoreCommittedDates])
  const applyDates = useCallback(() => {
    onDateChange(getExperienceDateRangeFromSearchState(searchState))
    closeMenu()
  }, [closeMenu, onDateChange, searchState])
  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSearchSubmit({ searchValue: searchValue.trim(), searchItem: getSubmitSearchItem(searchValue, searchItem), dates: getExperienceDateRangeFromSearchState(searchState) })
  }, [onSearchSubmit, searchItem, searchState, searchValue])

  const anytimeLabel = intl.formatMessage({
    id: 'RigImioe5lWt2RsFEZL9',
    defaultMessage: 'Anytime',
    description: 'Placeholder for date field indicating any date is acceptable',
  })
  const searchButtonLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })
  const experienceDateFieldValue = useExperienceDateDisplayValue(searchState.checkinDate, searchState.checkoutDate)
  const experienceTripDateRangeMoments = useMemo(() => experienceTripDateRange ? {
    start: moment(experienceTripDateRange.start), end: moment(experienceTripDateRange.end),
  } : undefined, [experienceTripDateRange])

  return {
    searchState,
    searchDispatch,
    controlsContainerRef,
    experienceDateTriggerRef,
    isCompactDatePicker,
    isExperienceDatePanelOpen,
    openDates,
    cancelDates,
    applyDates,
    handleSubmit,
    anytimeLabel,
    searchButtonLabel,
    experienceWhenLabel: whenLabel ?? anytimeLabel,
    experienceDateFieldValue,
    experienceTripDateRangeMoments,
  }
}

export default useExperienceListingSearchControls
